import React, { FC } from 'react';
import { useI18nContext } from './I18nContext';
import escapeTextForBrowser from './utils/escapeTextForBrowser';

class SafeString {
  value?: string;
  constructor(value?: string) {
    this.value = value;
  }

  toString() {
    return this.value == null ? '' : `${this.value}`;
  }

  toHtml() {
    return this.toString();
  }
}

function isSafeString(str: unknown): str is SafeString {
  return (str as SafeString)?.toHtml !== undefined;
}

function escape(string: string | boolean | number | SafeString) {
  // for null/undefined return empty string
  if (string == null) {
    return '';
  }

  // don't escape SafeStrings, since they're already safe
  if (isSafeString(string)) {
    return string.toHtml();
  }

  return escapeTextForBrowser(string);
}

const withInterpolationEscape = (payload: Record<string, any>) => ({
  ...payload,
  interpolation: {
    escape,
    escapeValue: true,
    useRawValueToEscape: true,
  },
});

const TAG_NAME_ALLOWED = ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'ul', 'ol', 'li'] as const;

type TransProps = {
  i18nKey: string;
  tagName?: (typeof TAG_NAME_ALLOWED)[number];
  className?: string;
  [key: string]: any;
};

/**
 * Allows to render HTML tags in translations
 */
const Trans: FC<TransProps> & { safeString: (value?: string) => SafeString } = ({
  tagName: TagName = TAG_NAME_ALLOWED[0],
  i18nKey,
  className,
  ...payload
}) => {
  const { t } = useI18nContext();
  const translation = t(i18nKey, withInterpolationEscape(payload));

  return <TagName className={className} dangerouslySetInnerHTML={{ __html: translation }} />;
};

/**
 * To wrap interpolation value that should not be escaped
 */
Trans.safeString = (value?: string) => new SafeString(value);

export default Trans;
